import React, { useContext, useEffect, useReducer, CSSProperties } from 'react';
import { Header } from './Header';
import {
	StepLabel,
	StepperActions,
	StepperContextProps,
	StepperProps,
	StepProps,
	StepStatus,
} from './types';

export function getStepStatus(
	currentStep: number,
	stepIndex: number
): StepStatus {
	if (currentStep === stepIndex) {
		return 'current';
	}
	if (currentStep < stepIndex) {
		return 'pending';
	}
	return 'completed';
}

const initialState = {
	currentStep: 1,
	stepLabels: [] as StepLabel[],
};

const StepperContext = React.createContext({} as StepperContextProps);

export const useStepper = (): StepperContextProps => {
	const context = useContext(StepperContext);
	if (!context) {
		throw new Error('useStepper must be used within a StepperProvider');
	}
	return context;
};

const reducer = (state: typeof initialState, action: StepperActions) => {
	switch (action.type) {
		case 'goBack':
			return {
				...state,
				currentStep: state.currentStep - 1,
			};
		case 'goNext':
			return {
				...state,
				currentStep: state.currentStep + 1,
			};
		case 'setStep':
			return {
				...state,
				currentStep: action.payload,
			};
		default:
			return state;
	}
};

export function Stepper({
	children,
	step = 1,
	style,
	stepsContainerStyle,
	...rest
}: StepperProps): JSX.Element {
	const { topBar = <Header />, bottomBar } = rest;
	const childrenArray = React.Children.toArray(children).filter(Boolean);
	const stepLabels = [] as StepLabel[];
	const steps = childrenArray.map((child, index) => {
		stepLabels.push({
			label: (child as React.ReactElement).props.label,
			description: (child as React.ReactElement).props.description,
		});
		return React.cloneElement(child as React.ReactElement, {
			index: index + 1,
			...(child as React.ReactElement).props,
		});
	});
	const [state, dispatch] = useReducer(reducer, {
		...initialState,
		currentStep: step,
		stepLabels: stepLabels,
	});

	useEffect(
		function updateStepByProp() {
			if (step !== state.currentStep)
				dispatch({ type: 'setStep', payload: step });
		},
		[step]
	);

	return (
		<div
			className="stepper stepper-pills d-flex flex-column justify-content-around"
			style={{ flex: 1, ...style }}
		>
			<StepperContext.Provider
				value={{
					stepLabels: state.stepLabels,
					currentStep: state.currentStep,
					goBack: () => dispatch({ type: 'goBack' }),
					goNext: () => dispatch({ type: 'goNext' }),
				}}
			>
				{topBar}
				<div
					style={{
						flex: 1,

						...stepsContainerStyle,
					}}
				>
					{steps[state.currentStep - 1]}
				</div>
				{bottomBar}
			</StepperContext.Provider>
		</div>
	);
}

function Step({
	component,
	index = 0,
	data,
	toggleModal,
}: StepProps): JSX.Element | null {
	const { currentStep, stepLabels, goBack, goNext } = useStepper();

	return component({
		goBack,
		goNext,
		isLast: currentStep === stepLabels.length,
		status: getStepStatus(currentStep, index),
		data,
		toggleModal,
	});
}

Stepper.Step = Step;

const createStepper = () => Stepper;

export default createStepper;
